import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { debounce } from 'lodash';

interface FaqData {
    question: string;
    answer: string;
}

type FaqDataArray = FaqData[];

@Component
export class FaqContainer extends ThemeComponent {
    @Ref data!: HTMLScriptElement;
    @Ref searchContainer!: HTMLElement;
    @Ref searchInput!: HTMLInputElement;
    @Ref searchResultsContainer!: HTMLElement;

    faqData: FaqDataArray|null = null;

    init() {
        // Init observer
        this.initObserver();

        // Attempt to parse JSON
        this.faqData = this.parseData();
        if (this.faqData === null) {
            this.$log.error('FAQ - unable to access FAQ data');
            return;
        }

        // Listen for search input
        this.handleSearch();

        // Handle close events
        this.handleCloseEvents();

        const theme = document.body.getAttribute('data-theme') as string;
        if (theme === 'nova') {
            this.initCategoryHandlers();
        }
    }

    initObserver() {
        const observer = new MutationObserver(() => {
            // eslint-disable-next-line max-len
            const resultItems = this.searchResultsContainer.querySelectorAll('.faq__search__results-item:not(.faq__search__results-item--no-results)');
            for (const resultItem of resultItems) {
                const resultItemElement = resultItem as HTMLElement;
                this.$listen(resultItemElement, 'click', () => {
                    this.handleResultClick(resultItemElement);
                });
            }
        });
        observer.observe(this.searchResultsContainer, {
            subtree: true,
            childList: true
        });
    }

    handleResultClick(resultItemElement: HTMLElement) {

        // Remove classes from search
        this.searchContainer.classList.remove('is-active');
        document.body.classList.remove('overlay-active');

        // Handleize the selected item title so we can find the correct FAQ item
        let selectedItemContent = resultItemElement.innerHTML.toLowerCase();
        selectedItemContent = selectedItemContent.replace('&amp;', '');
        selectedItemContent = selectedItemContent.replace('&', '');
        selectedItemContent = selectedItemContent.replace(/[^\w\s]/gi, '');
        selectedItemContent = selectedItemContent.replace(/[^a-z0-9]+/g, '-');
        selectedItemContent = selectedItemContent.replace(/-$/, '');
        selectedItemContent = selectedItemContent.replace(/^-/, '');

        // Open the correct FAQ accordion item
        const titleIdentifier = selectedItemContent;
        const matchingFaqItem: HTMLElement|null = this.querySelector(`[data-faq-identifier=${titleIdentifier}]`);
        if (matchingFaqItem === null) {
            return;
        }
        const accordionItem = matchingFaqItem.querySelector('.accordion ');
        if (accordionItem && !accordionItem.classList.contains('accordion--open')) {
            const accordionHeader: HTMLElement|null = matchingFaqItem.querySelector('.accordion__header');
            if (accordionHeader) {
                accordionHeader.click();
            }
        }

        // Scroll page to the correct FAQ accordion item
        const elementRect = matchingFaqItem.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2);
        window.scroll({
            top: middle,
            left: 0,
            behavior: 'smooth'
        });
    }

    parseData() {
        let data = null;
        if (this.data) {
            try {
                data = JSON.parse(this.data.innerHTML);
            } catch(event) {
                this.$log.error('FAQ parse error', {
                    event
                });
            }
        }
        return data;
    }

    handleSearch() {
        this.$listen(this.searchInput, 'input', debounce((e) => {
            this.searchContainer.classList.add('is-active');
            document.body.classList.add('overlay-active');
            const enteredText = this.searchInput.value;
            const results = this.filterResults(enteredText);
            this.updateResultsMarkup(results);
        }));
    }

    filterResults(enteredText: string) {
        let results: string[] = [];
        if (this.faqData !== null) {
            for (const data of this.faqData) {
                const valueToCheck = enteredText.toLowerCase();
                const questionToCheck = data.question.toLowerCase();
                const answerToCheck = data.answer.toLowerCase();
                if (questionToCheck.includes(valueToCheck) || answerToCheck.includes(valueToCheck)) {
                    results.push(data.question);
                }
            }
        }
        results = results.slice(0, 5);
        return results;
    }

    updateResultsMarkup(results: string[]) {
        let resultsMarkup = '';
        for (const result of results) {
            const resultMarkup = `
                <div class="faq__search__results-item">
                    ${result}
                </div>
            `;
            resultsMarkup += resultMarkup;
        }
        if (resultsMarkup === '') {
            resultsMarkup = `
                <div class="faq__search__results-item faq__search__results-item--no-results">
                    No Results Found
                </div>
            `;
        }
        this.searchResultsContainer.innerHTML = resultsMarkup;
    }

    handleCloseEvents() {
        this.$listen(document.body, 'click', (e) => {
            const clickedElement = e.target as HTMLElement;
            if (this.searchContainer === clickedElement || this.searchContainer.contains(clickedElement)) {
                return;
            }
            this.searchContainer.classList.remove('is-active');
            document.body.classList.remove('overlay-active');
        });
    }

    // Nova Theme Additions
    initCategoryHandlers() {
        const categories = this.querySelectorAll('.faq__category-list__title');
        for (const category of categories) {
            this.$listen(category as HTMLElement, 'click', this.onCategoryClicked.bind(this));
        }
    }

    onCategoryClicked(event: MouseEvent) {
        const activeCategoryButton = this.querySelector('.faq__category-list__title--selected') as HTMLElement;
        const newActiveCategoryButton = event.target as HTMLElement;
        const categoryIndex = newActiveCategoryButton .getAttribute('data-cat-index');
        const oldActiveCategory = this.querySelector(`.faq__category-contents--selected`) as HTMLElement;
        const newActiveCategory = this.querySelector(
            `.faq__category-contents[data-cat-index='${categoryIndex}']`
        ) as HTMLElement;

        activeCategoryButton.classList.remove('faq__category-list__title--selected');
        newActiveCategoryButton.classList.add('faq__category-list__title--selected');
        oldActiveCategory.classList.remove('faq__category-contents--selected');
        newActiveCategory.classList.add('faq__category-contents--selected');
    }
}
